/**
 * Client-wise social timeline — chronological (newest first) list of
 * published + scheduled FB/IG posts for one client group.
 * Pure read layer over db.social_posts; no pipeline changes.
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { socialAPI, clientGroupAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Facebook, Instagram, CalendarClock, CheckCircle2, XCircle,
  History, ArrowRight, CalendarDays,
} from 'lucide-react';
import { formatTaskDate } from '../lib/dateFormat';

const STATUS_CLS = {
  scheduled: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  publishing: 'bg-gold/15 text-gold border-gold/40',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  partial: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  failed: 'bg-red-500/15 text-red-300 border-red-500/40',
  draft: 'bg-foreground/10 text-foreground/70 border-border/40',
};

export const effectiveDate = (p) =>
  (p.status === 'scheduled' ? p.scheduled_at : null) || p.publish_finished_at || p.scheduled_at || p.created_at || '';

export function postPlatforms(p) {
  const s = new Set();
  (p.targets || []).forEach((t) => (t.platforms || []).forEach((pl) => s.add(pl)));
  return s;
}

export function TimelineEntry({ post, onOpenQueue }) {
  const plats = postPlatforms(post);
  const dt = effectiveDate(post);
  const future = post.status === 'scheduled';
  return (
    <div className="flex gap-3" data-testid={`timeline-entry-${post.id}`}>
      <div className="flex flex-col items-center">
        <div className={`h-3 w-3 rounded-full mt-1.5 shrink-0 ${future ? 'bg-blue-400' : post.status === 'published' ? 'bg-emerald-400' : post.status === 'failed' ? 'bg-red-400' : 'bg-gold'}`} />
        <div className="w-px flex-1 bg-border/40" />
      </div>
      <Card className="flex-1 mb-3">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              {future ? <CalendarClock className="h-3 w-3" /> : post.status === 'published' ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : post.status === 'failed' ? <XCircle className="h-3 w-3 text-red-400" /> : null}
              {formatTaskDate(dt)}
            </span>
            <Badge variant="outline" className={`text-[9px] uppercase ${STATUS_CLS[post.status] || ''}`}>{post.status}</Badge>
            <Badge variant="outline" className="text-[9px] uppercase">{post.post_type || 'feed'}</Badge>
            {plats.has('facebook') && <Facebook className="h-3.5 w-3.5 text-[#4d94ff]" />}
            {plats.has('instagram') && <Instagram className="h-3.5 w-3.5 text-pink-300" />}
          </div>
          <p className="text-[12px] text-foreground/85 mt-1 line-clamp-2">
            {post.caption || <span className="italic text-muted-foreground">(no caption)</span>}
          </p>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-[10px] text-muted-foreground">
              {(post.targets || []).length} account{(post.targets || []).length === 1 ? '' : 's'}
              {post.media?.length ? ` · ${post.media.length} media` : ''}
            </span>
            <button type="button" onClick={onOpenQueue}
                    className="text-[10px] text-gold hover:underline inline-flex items-center gap-0.5"
                    data-testid={`timeline-open-queue-${post.id}`}>
              Open in Queue <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ClientSocialTimelinePage() {
  const { clientGroupId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    if (!wsId || !clientGroupId) return;
    setLoading(true);
    socialAPI.listPosts(wsId, { client_group_id: clientGroupId, limit: 200 })
      .then((r) => setPosts(r?.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    clientGroupAPI.list(wsId)
      .then((r) => setClientName(((r?.data || []).find((c) => c.id === clientGroupId) || {}).name || ''))
      .catch(() => {});
  }, [wsId, clientGroupId]);

  const sorted = useMemo(
    () => [...posts]
      .filter((p) => p.status !== 'draft')
      .sort((a, b) => (effectiveDate(b) || '').localeCompare(effectiveDate(a) || '')),
    [posts],
  );

  return (
    <div className="p-6 space-y-5 animate-fade-in-up max-w-3xl" data-testid="client-social-timeline-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <History className="h-6 w-6 text-gold" /> {clientName || 'Client'} — Social Timeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Published and scheduled Facebook + Instagram content, newest first.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-[11px]"
                  onClick={() => navigate(`/clients/${clientGroupId}/content-calendar`)}
                  data-testid="timeline-goto-calendar">
            <CalendarDays className="h-3.5 w-3.5 mr-1" /> Content Calendar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold/50" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="timeline-empty">
          No published or scheduled posts for this client yet.
        </div>
      ) : (
        <div data-testid="timeline-list">
          {sorted.map((p) => (
            <TimelineEntry key={p.id} post={p} onOpenQueue={() => navigate('/queue')} />
          ))}
        </div>
      )}
    </div>
  );
}
